﻿import React, { useContext, useState, useRef, useEffect, useCallback, useMemo } from 'react';
import Context, { context_type } from '@Util/Context';
import { useTranslation } from 'react-i18next';
import { Tooltip } from 'react-tooltip';
import { Icon_Account_Circle } from '@Elements/ImageElements/Icons';

import User_Details from '@Elements/DynamicIsland/User_Details';
import User_Navigation from '@Elements/DynamicIsland/User_Navigation';

const User_Menu: React.FC = React.memo(() => {
    const [t] = useTranslation();
    const { logged_in_user }: context_type = useContext(Context);

    const user_menu_ref = useRef<HTMLDivElement>(null);
    const [menu_is_active, set_menu_is_active] = useState<boolean>(false);

    const handleClickOutside = useCallback((e: MouseEvent) => {
        if (user_menu_ref.current && !user_menu_ref.current.contains(e.target as Node) && menu_is_active) {
            set_menu_is_active(false);
        }
    }, [menu_is_active]);

    useEffect(() => {
        document.addEventListener('click', handleClickOutside);
        return () => {
            document.removeEventListener('click', handleClickOutside);
        };
    }, [handleClickOutside]);

    const toggleMenu = useCallback(() => {
        set_menu_is_active(prev => !prev);
    }, []);

    const tooltipContent = useMemo(() => {
        return `${t('Text.LoggedInAs')}: ${logged_in_user?.firstName || ''} ${logged_in_user?.lastName || ''}`;
    }, [t, logged_in_user]);

    const menuTitle = useMemo(() => {
        return t('Hover.ClickToOpenUserMenu');
    }, [menu_is_active, t]);

    return (
        <div className="Dynamic_Island_User_Menu">
            <button
                type="button"
                className="Dynamic_Island_User_Menu_Toggle"
                title={menuTitle}
                onClick={toggleMenu}
            >
                <span
                    data-tooltip-id="current_user_tooltip"
                    data-tooltip-content={tooltipContent}
                    data-tooltip-place="top"
                    data-tooltip-delay-show={600}
                >
                    {
                        logged_in_user.photo.fileUrl ?
                            <img
                                src={logged_in_user.photo.fileUrl}
                                alt={`${logged_in_user?.firstName || ''} ${logged_in_user?.lastName || ''}`}
                            /> :
                            <Icon_Account_Circle />
                    }
                    <Tooltip id="current_user_tooltip" />
                </span>
            </button>
            {
                menu_is_active &&
                <nav ref={user_menu_ref} className="Dynamic_Island_User_Menu_Wrapper">
                    <User_Details />
                    <User_Navigation set_menu_is_active={set_menu_is_active} />
                </nav>
            }
        </div>
    );
});

export default User_Menu;